import { useBuffer } from '@/composables/buffer'
import { themeService } from '@/services/api'
import storeConfigs from 'constants/storeConfigs.ts'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useBackendStore } from './backend'
import { useClientStore } from './client'

const defaultTheme: Api.ClientSetting = {
	bgcolor_f: null,
	bgcolor_t: null,
	color_a: null,
	color_l: null,
	color_t: null,
	color_z: null,
	greetings_text: null,
	logo: null,
}

type Status = 'initial' | 'loading' | 'error' | 'ready'

export const useThemeStore = defineStore(
	storeConfigs.theme.name,
	() => {
		const status = ref<Status>('initial')
		const theme = ref<Api.ClientSetting & { domain?: string }>()
		const logo = ref<undefined | string | null>(undefined)
		const defaultLogo = ref<string | null>('')
		const error = ref()
		const name = ref()

		const getLogo = computed(() => theme.value?.logo)

		const greetingText = computed(() => theme.value?.greetings_text)

		function setError(value: any) {
			error.value = value
		}

		function setLogo(value: string | null) {
			logo.value = value
		}

		function setTheme(value: Api.ClientSetting) {
			theme.value = value
		}

		function setStatus(value: Status) {
			status.value = value
		}

		async function initialise() {
			try {
				await setDefaultLogo()

				if (useBackendStore().url?.subdomain) {
					const themeSettings = await loadTheme()
					setTheme(themeSettings)

					const logo = await loadLogo()
					setLogo(logo)
				}
			} catch (error) {
				setError(error)
				theme.value = undefined
				if (!logo.value) {
					logo.value = null
				}
				status.value = 'error'
			}
		}

		async function loadTheme() {
			status.value = 'loading'
			return themeService
				.get()
				.then(({ data }) => {
					setStatus('ready')
					setError(undefined)
					setTheme(data)

					return data
				})
				.catch((error) => {
					setStatus('error')
					setError(error)
					throw error
				})
		}

		async function setDefaultLogo() {
			defaultLogo.value = useClientStore().isHpDomain
				? ((await import('@/assets/images/logo/default.png'))?.default ?? null)
				: ((await await import('@/assets/images/logo/aaremoTextBottom.png'))
						?.default ?? null)
		}

		async function loadLogo() {
			if (!getLogo.value) {
				return defaultLogo.value
			} else {
				return themeService.getLogo().then(
					(logo) => {
						return useBuffer().buffer2Base64(logo)
					},
					(error) => {
						setError(error)
						setStatus('error')
						return null
					}
				)
			}
		}

		function $reset() {
			status.value = 'initial'
			// greetingText.value = undefined
			error.value = undefined
			theme.value = defaultTheme
		}

		return {
			status,
			name,
			theme,
			logo,
			defaultLogo,
			error,
			greetingText,
			getLogo,
			initialise,
			setError,
			setLogo,
			settings: setTheme,
			setStatus,
			load: loadTheme,
			loadLogo,
			$reset,
		}
	},
	{
		persist: storeConfigs.theme.persist,
	}
)

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useThemeStore, import.meta.hot))
}
